import React, { useState, useEffect } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { Loader2, FileText, Phone, Mail } from 'lucide-react';
import api from '../../api/auth.api';

interface Role {
  role_id: number;
  role_type: string;
  name: string;
  email: string;
  phone: string;
}

interface Task {
  task_id: number;
  task_type_id: number;
  type_name: string;
  type_code: string;
  status: string;
  assigned_company: string;
  assigned_contact: string;
  assigned_phone: string;
  notes: string;
  due_date: string;
  completed_date: string;
}

interface Submittal {
  submittal_id: number;
  source_name: string;
  item_name: string;
  status: string;
  due_date: string;
  notes: string;
}

interface Locate {
  locate_id: number;
  ticket_number: string;
  status: string;
  request_date: string;
  expiration_date: string;
  notes: string;
}

interface StartupSheet {
  startup_id: number;
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  general_contractor: string;
  status: string;
  estimator_name: string;
  estimator_email: string;
  completed_at: string;
  completed_by: string;
  approx_start_date: string;
  has_permit: boolean;
  contract_signed: boolean;
  mot_permit_required: boolean;
  is_dot_work: boolean;
  locates_called_in: boolean;
  locates_date: string;
  pre_meeting_required: boolean;
  work_estimation_description: string;
  gc_project_manager: string;
  gc_pm_phone: string;
  gc_pm_email: string;
  superintendent_name: string;
  superintendent_phone: string;
  superintendent_email: string;
  is_asphalt_project: boolean;
  roles: Role[];
  tasks: Task[];
  submittals: Submittal[];
  locates: Locate[];
}

// Get passed jobId from navigation
const usePassedJobId = () => {
  const routerLocation = useRouterLocation();
  return routerLocation.state?.jobId as number | undefined;
};

// Format phone number
const formatPhone = (phone: string | null) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

export const StartupSheetViewer: React.FC = () => {
  const passedJobId = usePassedJobId();

  const [startupSheet, setStartupSheet] = useState<StartupSheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'TBD';
    // Check if it's a date string
    if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    return dateString.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Load startup sheet
  useEffect(() => {
    const fetchStartupSheet = async () => {
      if (!passedJobId) {
        setError('No job selected');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/api/job-startup/job/${passedJobId}`);
        if (response.data.success) {
          setStartupSheet(response.data.data);
        } else {
          setError('No startup sheet found for this job');
        }
      } catch (err: any) {
        console.error('Error fetching startup sheet:', err);
        if (err.response?.status === 404) {
          setError('No startup sheet found for this job');
        } else {
          setError('Failed to load startup sheet');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStartupSheet();
  }, [passedJobId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <Loader2 size={40} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b', marginTop: '16px' }}>Loading startup sheet...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <FileText size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
        <p style={{ color: '#64748b', fontSize: '16px' }}>{error}</p>
      </div>
    );
  }

  if (!startupSheet) return null;

  const assignedTasks = startupSheet.tasks?.filter(t => t.assigned_company && t.assigned_company !== 'Not Required') || [];
  const notRequiredTasks = startupSheet.tasks?.filter(t => t.assigned_company === 'Not Required') || [];
  const submittalCount = startupSheet.submittals?.length || 0;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', paddingBottom: '100px' }}>
      {/* New Job Banner - Green */}
      <div style={{
        backgroundColor: '#dcfce7',
        borderLeft: '5px solid #16a34a',
        padding: '20px'
      }}>
        <p style={{
          margin: '0 0 5px 0',
          fontSize: '13px',
          color: '#166534',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          {startupSheet.status === 'completed' ? 'Job Startup Complete' : 'Startup Sheet'}
        </p>
        <h2 style={{ margin: 0, fontSize: '24px', color: '#14532d', fontWeight: '700' }}>
          {startupSheet.job_name}
        </h2>
        {startupSheet.bradanna_job_number && (
          <p style={{ margin: '8px 0 0 0', fontSize: '18px', color: '#166534', fontWeight: '600' }}>
            Job #{startupSheet.bradanna_job_number}
          </p>
        )}
      </div>

      <div style={{ padding: '20px' }}>
        {/* Job Information Section */}
        <SectionTitle title="Job Information" color="#dc2626" />
        <DetailTable>
          <DetailRow label="Job Name" value={<strong>{startupSheet.job_name}</strong>} />
          {startupSheet.bradanna_job_number && (
            <DetailRow label="Job Number" value={startupSheet.bradanna_job_number} alt />
          )}
          <DetailRow
            label="Address"
            value={`${startupSheet.address || 'N/A'}${startupSheet.city ? `, ${startupSheet.city}` : ''}${startupSheet.state ? `, ${startupSheet.state}` : ''}`}
          />
          <DetailRow
            label="Job Type"
            value={
              <Badge
                text={startupSheet.is_asphalt_project ? 'ASPHALT' : 'SITE WORK'}
                bg={startupSheet.is_asphalt_project ? '#18181b' : '#0ea5e9'}
                color="#ffffff"
              />
            }
            alt
          />
          <DetailRow
            label="Target Start"
            value={<strong style={{ color: '#dc2626', fontSize: '16px' }}>{formatDate(startupSheet.approx_start_date)}</strong>}
          />
          <DetailRow label="Completed By" value={startupSheet.completed_by || startupSheet.estimator_name || 'N/A'} alt />
        </DetailTable>

        {/* GC Section */}
        {startupSheet.general_contractor && (
          <>
            <SectionTitle title="General Contractor" color="#2563eb" />
            <DetailTable>
              <DetailRow label="Company" value={<strong>{startupSheet.general_contractor}</strong>} />
              {startupSheet.superintendent_name && (
                <DetailRow label="Superintendent" value={startupSheet.superintendent_name} alt />
              )}
              {startupSheet.superintendent_phone && (
                <DetailRow
                  label="  Phone"
                  value={
                    <a href={`tel:${startupSheet.superintendent_phone}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                      {formatPhone(startupSheet.superintendent_phone)}
                    </a>
                  }
                />
              )}
              {startupSheet.superintendent_email && (
                <DetailRow
                  label="  Email"
                  value={
                    <a href={`mailto:${startupSheet.superintendent_email}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                      {startupSheet.superintendent_email}
                    </a>
                  }
                  alt
                />
              )}
              {startupSheet.gc_project_manager && (
                <DetailRow label="Project Manager" value={startupSheet.gc_project_manager} />
              )}
              {startupSheet.gc_pm_phone && (
                <DetailRow
                  label="  Phone"
                  value={
                    <a href={`tel:${startupSheet.gc_pm_phone}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                      {formatPhone(startupSheet.gc_pm_phone)}
                    </a>
                  }
                  alt
                />
              )}
            </DetailTable>
          </>
        )}

        {/* Project Status Section */}
        <SectionTitle title="Project Status" color="#f59e0b" />
        <DetailTable>
          <DetailRow
            label="GC Has Permit"
            value={
              <Badge
                text={startupSheet.has_permit ? 'YES' : 'NO PERMIT'}
                bg={startupSheet.has_permit ? '#dcfce7' : '#fef3c7'}
                color={startupSheet.has_permit ? '#166534' : '#92400e'}
              />
            }
          />
          <DetailRow
            label="Contract Signed"
            value={
              <Badge
                text={startupSheet.contract_signed ? 'YES' : 'NOT SIGNED'}
                bg={startupSheet.contract_signed ? '#dcfce7' : '#fef3c7'}
                color={startupSheet.contract_signed ? '#166534' : '#92400e'}
              />
            }
            alt
          />
          <DetailRow
            label="Locates"
            value={
              <Badge
                text={startupSheet.locates_called_in ? 'CALLED IN' : 'NOT CALLED'}
                bg={startupSheet.locates_called_in ? '#dcfce7' : '#fef3c7'}
                color={startupSheet.locates_called_in ? '#166534' : '#92400e'}
              />
            }
          />
          {startupSheet.mot_permit_required && (
            <DetailRow
              label="MOT Plan"
              value={<Badge text="REQUIRED" bg="#fef3c7" color="#92400e" />}
              alt
            />
          )}
          {startupSheet.is_dot_work && (
            <DetailRow
              label="DOT Road Work"
              value={<Badge text="YES - DOT" bg="#fef3c7" color="#92400e" />}
            />
          )}
          {startupSheet.pre_meeting_required && (
            <DetailRow
              label="Pre-Con Meeting"
              value={<Badge text="REQUIRED" bg="#fef3c7" color="#92400e" />}
              alt
            />
          )}
        </DetailTable>

        {/* Work Description */}
        {startupSheet.work_estimation_description && (
          <>
            <SectionTitle title="Work Description" color="#8b5cf6" />
            <div style={{
              backgroundColor: '#fafafa',
              border: '1px solid #e4e4e7',
              borderRadius: '4px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#3f3f46', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                {startupSheet.work_estimation_description}
              </p>
            </div>
          </>
        )}

        {/* Subcontractor Assignments */}
        <SectionTitle title="Subcontractor Assignments" color="#059669" />
        {assignedTasks.length > 0 ? (
          <div style={{
            border: '1px solid #e4e4e7',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '12px'
          }}>
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              backgroundColor: '#18181b',
              padding: '12px 16px'
            }}>
              <span style={{ fontSize: '12px', color: '#ffffff', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Task
              </span>
              <span style={{ fontSize: '12px', color: '#ffffff', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Assigned To
              </span>
            </div>
            {/* Rows */}
            {assignedTasks.map((task, idx) => {
              const isGcOwner = task.assigned_company === 'By General Contractor/Owner';
              return (
                <div
                  key={task.task_id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    padding: '14px 16px',
                    backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa',
                    borderBottom: idx < assignedTasks.length - 1 ? '1px solid #e4e4e7' : 'none'
                  }}
                >
                  <span style={{ fontSize: '14px', color: '#18181b', fontWeight: '600' }}>
                    {task.type_name}
                  </span>
                  <div>
                    {isGcOwner ? (
                      <Badge text="GC/OWNER" bg="#dbeafe" color="#1e40af" />
                    ) : (
                      <>
                        <span style={{ fontSize: '14px', color: '#18181b' }}>{task.assigned_company}</span>
                        {task.assigned_contact && (
                          <div style={{ fontSize: '13px', color: '#52525b', marginTop: '4px' }}>
                            {task.assigned_contact}
                          </div>
                        )}
                        {task.assigned_phone && (
                          <a
                            href={`tel:${task.assigned_phone}`}
                            style={{ fontSize: '13px', color: '#2563eb', textDecoration: 'none', display: 'block', marginTop: '2px' }}
                          >
                            {formatPhone(task.assigned_phone)}
                          </a>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ fontSize: '15px', color: '#71717a', marginBottom: '20px' }}>No subcontractor assignments.</p>
        )}

        {notRequiredTasks.length > 0 && (
          <p style={{ fontSize: '14px', color: '#71717a', marginBottom: '20px' }}>
            <strong>Not Required:</strong> {notRequiredTasks.map(t => t.type_name).join(', ')}
          </p>
        )}

        {/* Stats Boxes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '24px' }}>
          <div style={{ backgroundColor: '#18181b', padding: '20px', textAlign: 'center', borderRadius: '4px' }}>
            <div style={{ fontSize: '36px', fontWeight: '800', color: '#ffffff' }}>{assignedTasks.length}</div>
            <div style={{ fontSize: '12px', color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>
              Tasks Assigned
            </div>
          </div>
          <div style={{ backgroundColor: '#dc2626', padding: '20px', textAlign: 'center', borderRadius: '4px' }}>
            <div style={{ fontSize: '36px', fontWeight: '800', color: '#ffffff' }}>{submittalCount}</div>
            <div style={{ fontSize: '12px', color: '#fecaca', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>
              Submittals
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Section Title Component
const SectionTitle: React.FC<{ title: string; color: string }> = ({ title, color }) => (
  <div style={{
    borderBottom: `3px solid ${color}`,
    paddingBottom: '10px',
    marginTop: '24px',
    marginBottom: '12px'
  }}>
    <h2 style={{
      margin: 0,
      fontSize: '16px',
      fontWeight: '700',
      color: '#18181b',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    }}>
      {title}
    </h2>
  </div>
);

// Detail Table Component
const DetailTable: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    border: '1px solid #e4e4e7',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '8px'
  }}>
    {children}
  </div>
);

// Detail Row Component
const DetailRow: React.FC<{ label: string; value: React.ReactNode; alt?: boolean }> = ({ label, value, alt }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '40% 60%',
    borderBottom: '1px solid #e4e4e7'
  }}>
    <div style={{
      padding: '12px 16px',
      backgroundColor: alt ? '#fafafa' : '#ffffff',
      fontSize: '14px',
      color: '#52525b',
      fontWeight: '600'
    }}>
      {label}
    </div>
    <div style={{
      padding: '12px 16px',
      backgroundColor: alt ? '#fafafa' : '#ffffff',
      fontSize: '14px',
      color: '#18181b'
    }}>
      {value}
    </div>
  </div>
);

// Badge Component
const Badge: React.FC<{ text: string; bg: string; color: string }> = ({ text, bg, color }) => (
  <span style={{
    display: 'inline-block',
    backgroundColor: bg,
    color: color,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '700'
  }}>
    {text}
  </span>
);
